import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Star, Tag, ExternalLink, Loader2 } from 'lucide-react';
import { shopAPI, categoriesAPI, trackingAPI } from '../services/api';
import { toast } from 'sonner';

const Butikker = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [shops, setShops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchCategories();
    fetchShops();
  }, [selectedCategory, searchTerm, page]);

  const fetchCategories = async () => {
    try {
      const data = await categoriesAPI.getAll();
      setCategories(data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchShops = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 12,
      };
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const data = await shopAPI.getAll(params);
      setShops(data.shops);
    } catch (error) {
      console.error('Error fetching shops:', error);
      toast.error('Kunne ikke hente butikker');
    } finally {
      setLoading(false);
    }
  };

  const handleShopClick = async (shop) => {
    try {
      const response = await trackingAPI.trackClick(shop.id);
      // Redirect to affiliate link
      window.open(response.affiliate_link, '_blank');
      toast.success(`Redirecting til ${shop.name}`);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const handleCodeCopy = async (codeId, code) => {
    try {
      await navigator.clipboard.writeText(code);
      await trackingAPI.trackCodeCopy(codeId);
      toast.success(`Kopierede: ${code}`);
    } catch (error) {
      console.error('Error copying code:', error);
      toast.error('Kunne ikke kopiere kode');
    }
  };

  const filteredShops = shops;

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Header */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
                Rabatkoder
              </span>
              {' '}Til Alle Butikker
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Gennemse over 10.000 butikker og find de bedste rabatkoder til dine køb
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Søg efter butikker..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 text-lg border-2 focus:border-blue-500 rounded-xl"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.id)}
                className={`rounded-full transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-blue-600 to-teal-500 text-white shadow-lg'
                    : 'border-2 hover:border-blue-500 hover:text-blue-600'
                }`}
              >
                {category.name}
                <Badge variant="secondary" className="ml-2">
                  {category.count}
                </Badge>
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Shop Grid */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">
              Viser <span className="font-semibold text-gray-900">{loading ? '...' : filteredShops.length}</span> butikker
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            </div>
          ) : filteredShops.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Ingen butikker fundet</h3>
              <p className="text-gray-600">Prøv at søge efter noget andet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredShops.map((shop) => (
                <Card key={shop.id} className="group cursor-pointer border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                  <CardContent className="p-0">
                    {/* Shop Image */}
                    <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                      <img
                        src={shop.logo_url}
                        alt={shop.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-green-500 text-white border-none shadow-lg">
                          Op til {shop.commission_rate}% rabat
                        </Badge>
                      </div>
                    </div>

                    {/* Shop Info */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                            {shop.name}
                          </h3>
                          <Badge variant="outline" className="text-xs">
                            {shop.category}
                          </Badge>
                        </div>
                      </div>

                      {/* Description */}
                      {shop.description && (
                        <p className="text-sm text-gray-600 mb-4">{shop.description}</p>
                      )}

                      {/* Action Button */}
                      <Button 
                        onClick={() => handleShopClick(shop)}
                        className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white"
                      >
                        Se Rabatkoder
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Load More */}
      {filteredShops.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 mt-12">
          <div className="max-w-7xl mx-auto text-center">
            <Button variant="outline" size="lg" className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50">
              Indlæs Flere Butikker
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

export default Butikker;